import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, HardHat, CheckCircle2, Clock, ShieldAlert, GraduationCap } from "lucide-react";
import api from "../lib/api";
import { PageHeader } from "../components/AppLayout";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { StatCard } from "../components/shared";
import EntityDrawer from "../components/EntityDrawer";
import { FileUploader, FileChip } from "../components/module-widgets";

const badge = (s) => ({
  approved: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  pending: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
  rejected: "bg-red-500/10 text-red-600 dark:text-red-400",
}[s] || "bg-secondary text-muted-foreground");

const FIELDS = [
  { key: "company", label: "Company", type: "text" },
  { key: "contact", label: "Contact", type: "text" },
  { key: "status", label: "Status", type: "select", options: [{ value: "pending", label: "Pending" }, { value: "approved", label: "Approved" }, { value: "rejected", label: "Rejected" }] },
  { key: "insurance_expiry", label: "Insurance expiry", type: "date" },
  { key: "insurance_valid", label: "Insurance valid", type: "switch" },
  { key: "rams_uploaded", label: "RAMS uploaded", type: "switch" },
  { key: "induction_complete", label: "Induction complete", type: "switch" },
];

export default function Contractors() {
  const [summary, setSummary] = useState(null);
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [sel, setSel] = useState(null);

  const load = async () => {
    const [s, c] = await Promise.all([api.get("/contractors/summary"), api.get("/contractors")]);
    setSummary(s.data); setList(c.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.company) return;
    try {
      const exp = form.insurance_expiry ? new Date(form.insurance_expiry) : null;
      await api.post("/contractors", { ...form, status: "pending", insurance_expiry: exp ? exp.toISOString() : null, insurance_valid: exp ? exp > new Date() : true, rams_uploaded: false, induction_complete: false });
      toast.success("Contractor added"); setOpen(false); setForm({}); load();
    } catch { toast.error("Failed to save"); }
  };

  const setStatus = async (e, id, status) => { e.stopPropagation(); await api.patch(`/contractors/${id}`, { status }); toast.success(`Contractor ${status}`); load(); };

  if (!summary) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--accent))]" /></div>;

  const renderExtra = (state, setState) => (
    <div className="space-y-4">
      <div>
        <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Insurance certificate</div>
        {state.insurance_cert && <div className="mb-2"><FileChip file={state.insurance_cert} /></div>}
        <FileUploader label="Upload insurance" testid="upload-insurance" onUploaded={(f) => setState((s) => ({ ...s, insurance_cert: f }))} />
      </div>
      <div>
        <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Certificates &amp; documents</div>
        {(state.certs || []).length > 0 && <div className="mb-2 flex flex-wrap gap-2">{(state.certs || []).map((c, i) => <FileChip key={i} file={c} />)}</div>}
        <FileUploader label="Upload certificate" testid="upload-cert" onUploaded={(f) => setState((s) => ({ ...s, certs: [...(s.certs || []), f] }))} />
      </div>
    </div>
  );

  return (
    <div>
      <PageHeader title="Contractor Portal" subtitle="Click a contractor to edit details, upload certificates and add notes.">
        <Button className="rounded-full" onClick={() => { setForm({}); setOpen(true); }} data-testid="new-contractor-btn"><Plus className="mr-2 h-4 w-4" /> Add Contractor</Button>
      </PageHeader>

      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon={CheckCircle2} label="Approved" value={summary.approved} tone="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" />
        <StatCard icon={Clock} label="Pending approval" value={summary.pending} tone="bg-amber-500/10 text-amber-600 dark:text-amber-400" />
        <StatCard icon={ShieldAlert} label="Insurance expiring" value={summary.insurance_expiring} tone="bg-red-500/10 text-red-600 dark:text-red-400" />
        <StatCard icon={GraduationCap} label="Inductions complete" value={summary.inductions_complete} />
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
        {list.map((c) => (
          <Card key={c.id} onClick={() => setSel(c)} data-testid={`contractor-card-${c.id}`}
            className="cursor-pointer rounded-2xl border-border p-5 transition-all hover:-translate-y-1 hover:shadow-md">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-secondary text-[hsl(var(--accent))]"><HardHat className="h-5 w-5" /></div>
                <div><div className="font-heading text-sm font-semibold">{c.company}</div><div className="text-xs text-muted-foreground">{c.contact}</div></div>
              </div>
              <Badge className={`rounded-full capitalize ${badge(c.status)}`}>{c.status}</Badge>
            </div>
            <div className="mt-4 space-y-2 text-xs">
              <Row ok={c.insurance_valid} label={`Insurance ${c.insurance_valid ? "valid" : "expiring/expired"}`} />
              <Row ok={c.rams_uploaded} label="RAMS uploaded" />
              <Row ok={c.induction_complete} label="Induction complete" />
            </div>
            {c.status === "pending" && (
              <div className="mt-4 flex gap-2">
                <Button size="sm" className="flex-1 rounded-full text-xs" onClick={(e) => setStatus(e, c.id, "approved")} data-testid={`approve-${c.id}`}>Approve</Button>
                <Button size="sm" variant="outline" className="flex-1 rounded-full text-xs" onClick={(e) => setStatus(e, c.id, "rejected")} data-testid={`reject-${c.id}`}>Reject</Button>
              </div>
            )}
          </Card>
        ))}
      </div>

      {sel && (
        <EntityDrawer open={!!sel} onClose={() => setSel(null)} title={sel.company} collection="contractors"
          patchBase="/contractors" fields={FIELDS} item={sel} onSaved={() => load()} renderExtra={renderExtra} />
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Add Contractor</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Company</Label><Input value={form.company || ""} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="contractor-company" /></div>
            <div className="space-y-1.5"><Label>Contact name</Label><Input value={form.contact || ""} onChange={(e) => setForm({ ...form, contact: e.target.value })} data-testid="contractor-contact" /></div>
            <div className="space-y-1.5"><Label>Insurance expiry</Label><Input type="date" value={form.insurance_expiry || ""} onChange={(e) => setForm({ ...form, insurance_expiry: e.target.value })} data-testid="contractor-insurance" /></div>
          </div>
          <DialogFooter><Button onClick={save} className="rounded-full" data-testid="contractor-save-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Row = ({ ok, label }) => (
  <div className="flex items-center gap-2">
    {ok ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <ShieldAlert className="h-4 w-4 text-red-500" />}
    <span className={ok ? "text-foreground" : "text-muted-foreground"}>{label}</span>
  </div>
);
